// src/components/onboarding/OnboardingAdminPanel.tsx
//
// Admin panel for Owner Onboarding, rendered as a tab inside AdminDashboard.
// - Create a new onboarding (emails the owner a link).
// - Dashboard listing all onboardings with status + per-step progress.
// - Copy link, resend link, delete, and view sensitive data.
// Uses the shared `api` instance (admin JWT) via ownerOnboardingService.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Plus, Loader, Copy, Check, Send, Trash2, Eye, X, RefreshCw, ExternalLink,
    FileEdit, UserCog, Download, FileText, ShieldCheck,
} from 'lucide-react';
import {
    adminCreateOnboarding, adminListOnboardings, adminResendLink,
    adminDeleteOnboarding, adminGetOnboardingData,
    adminAddAmendment, adminRemoveAmendment, adminUpdateProfile,
} from '../../services/ownerOnboardingService';
import {
    AdminOnboarding, CreateOnboardingRequest, OnboardingOwnerData,
    AmendmentDocKey, AMENDMENT_DOC_LABELS, AdminProfileUpdate,
} from '../../types';

const AMENDMENT_DOC_KEYS: AmendmentDocKey[] = ['cohosting', 'owner-form', 'property-setup'];

const EMPTY_CREATE: CreateOnboardingRequest = {
    ownerFirstName: '', ownerLastName: '', ownerEmail: '', propertyAddress: '',
    signatureMode: 'DRAW_OR_UPLOAD',
};

const SIGNATURE_MODE_LABELS: Record<string, string> = {
    DRAW_OR_UPLOAD: 'Draw / upload',
    OTP: 'Email OTP',
};

const SIGNATURE_METHOD_LABELS: Record<string, string> = {
    DRAWN: 'Drawn',
    UPLOADED: 'Uploaded',
};

const StepPill: React.FC<{ label: string; done?: boolean }> = ({ label, done }) => (
    <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${
            done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
        }`}
    >
        {done && <Check className="w-3 h-3" />} {label}
    </span>
);

const OnboardingAdminPanel: React.FC = () => {
    const [items, setItems] = useState<AdminOnboarding[]>([]);
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const [showCreate, setShowCreate] = useState(false);
    const [createData, setCreateData] = useState<CreateOnboardingRequest>(EMPTY_CREATE);
    const [creating, setCreating] = useState(false);

    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const [detail, setDetail] = useState<OnboardingOwnerData | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Gestione note di modifica (modal)
    const [amendTarget, setAmendTarget] = useState<AdminOnboarding | null>(null);
    const [amendDoc, setAmendDoc] = useState<AmendmentDocKey>('cohosting');
    const [amendRef, setAmendRef] = useState('');
    const [amendText, setAmendText] = useState('');
    const [amendSaving, setAmendSaving] = useState(false);

    // Modifica anagrafica diretta (modal)
    const [editTarget, setEditTarget] = useState<AdminOnboarding | null>(null);
    const [editForm, setEditForm] = useState<AdminProfileUpdate>({});
    const [editSaving, setEditSaving] = useState(false);

    // Documenti firmati (modal download)
    const [docsTarget, setDocsTarget] = useState<AdminOnboarding | null>(null);

    const notify = (type: 'success' | 'error', message: string) => setFeedback({ type, message });

    useEffect(() => {
        if (!feedback) return;
        const t = setTimeout(() => setFeedback(null), 3500);
        return () => clearTimeout(t);
    }, [feedback]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const list = await adminListOnboardings();
            setItems(Array.isArray(list) ? list : []);
        } catch {
            notify('error', 'Could not load onboardings.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        try {
            await adminCreateOnboarding(createData);
            notify('success', 'Onboarding created and link emailed to the owner.');
            setCreateData(EMPTY_CREATE);
            setShowCreate(false);
            await load();
        } catch {
            notify('error', 'Could not create the onboarding.');
        } finally {
            setCreating(false);
        }
    };

    const buildLink = (item: AdminOnboarding) =>
        item.link || `${window.location.origin}/owner-onboarding/${item.publicSlug}`;

    const handleCopy = async (item: AdminOnboarding) => {
        try {
            await navigator.clipboard.writeText(buildLink(item));
            setCopiedId(item.id);
            setTimeout(() => setCopiedId((c) => (c === item.id ? null : c)), 2000);
        } catch {
            notify('error', 'Could not copy the link.');
        }
    };

    const handleResend = async (item: AdminOnboarding) => {
        setResendingId(item.id);
        try {
            await adminResendLink(item.id);
            notify('success', `Link re-sent to ${item.ownerEmail}.`);
        } catch {
            notify('error', 'Could not resend the link.');
        } finally {
            setResendingId(null);
        }
    };

    const handleDelete = async (item: AdminOnboarding) => {
        if (!window.confirm(`Delete the onboarding for ${item.ownerEmail}? This cannot be undone.`)) return;
        try {
            await adminDeleteOnboarding(item.id);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
            notify('success', 'Onboarding deleted.');
        } catch {
            notify('error', 'Could not delete the onboarding.');
        }
    };

    const handleViewData = async (item: AdminOnboarding) => {
        setDetailLoading(true);
        setDetail(null);
        try {
            const data = await adminGetOnboardingData(item.id);
            setDetail(data);
        } catch {
            notify('error', 'Could not load sensitive data.');
        } finally {
            setDetailLoading(false);
        }
    };

    const errMsg = (err: unknown, fallback: string): string => {
        const anyErr = err as { response?: { data?: { message?: string } } };
        return anyErr?.response?.data?.message || fallback;
    };

    /** Sostituisce l'item nella lista (e nel target del modal, se aperto) con la versione aggiornata dal backend. */
    const replaceItem = (updated: AdminOnboarding) => {
        setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
        setAmendTarget((t) => (t && t.id === updated.id ? updated : t));
    };

    const handleAddAmendment = async () => {
        if (!amendTarget) return;
        if (!amendText.trim()) {
            notify('error', 'Modification text is required.');
            return;
        }
        setAmendSaving(true);
        try {
            const updated = await adminAddAmendment(amendTarget.id, {
                document: amendDoc,
                reference: amendRef.trim(),
                text: amendText.trim(),
            });
            replaceItem(updated);
            setAmendRef('');
            setAmendText('');
            notify('success', 'Modification added.');
        } catch (err) {
            notify('error', errMsg(err, 'Could not add the modification.'));
        } finally {
            setAmendSaving(false);
        }
    };

    const handleRemoveAmendment = async (doc: AmendmentDocKey, index: number) => {
        if (!amendTarget) return;
        try {
            const updated = await adminRemoveAmendment(amendTarget.id, doc, index);
            replaceItem(updated);
            notify('success', 'Modification removed.');
        } catch (err) {
            notify('error', errMsg(err, 'Could not remove the modification.'));
        }
    };

    const openEdit = (item: AdminOnboarding) => {
        setEditForm({
            ownerFirstName: item.ownerFirstName || '',
            ownerLastName: item.ownerLastName || '',
            ownerEmail: item.ownerEmail || '',
            propertyAddress: item.propertyAddress || '',
        });
        setEditTarget(item);
    };

    const handleSaveEdit = async () => {
        if (!editTarget) return;
        if (editForm.ownerEmail && !editForm.ownerEmail.includes('@')) {
            notify('error', 'Please enter a valid email address.');
            return;
        }
        setEditSaving(true);
        try {
            const updated = await adminUpdateProfile(editTarget.id, {
                ownerFirstName: editForm.ownerFirstName?.trim(),
                ownerLastName: editForm.ownerLastName?.trim(),
                ownerEmail: editForm.ownerEmail?.trim(),
                propertyAddress: editForm.propertyAddress?.trim(),
            });
            replaceItem(updated);
            setEditTarget(null);
            notify('success', 'Owner details updated.');
        } catch (err) {
            notify('error', errMsg(err, 'Could not update the details.'));
        } finally {
            setEditSaving(false);
        }
    };

    /** Conta le note totali di un onboarding (per il badge del pulsante). */
    const amendmentCount = (item: AdminOnboarding): number =>
        AMENDMENT_DOC_KEYS.reduce((n, k) => n + (item.amendments?.[k]?.length ?? 0), 0);

    return (
        <div className="space-y-6">
            {feedback && (
                <div
                    className={`p-4 rounded-xl flex items-center gap-3 text-sm font-bold ${
                        feedback.type === 'success'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                    }`}
                >
                    {feedback.message}
                </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowCreate((s) => !s)}
                        className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-xl font-bold hover:bg-[#c42e40] shadow-md transition-all"
                    >
                        <Plus className="w-5 h-5" /> New onboarding
                    </button>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 border border-gray-300 text-gray-600 px-4 py-2.5 rounded-xl font-bold hover:bg-gray-100 transition-all"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
                <span className="text-sm text-gray-400 font-medium">{items.length} total</span>
            </div>

            {/* Create form */}
            {showCreate && (
                <form onSubmit={handleCreate} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
                    <h3 className="font-serif font-bold text-lg text-primary">Create a new onboarding</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">First name <span className="text-gray-400 normal-case font-medium">(optional)</span></label>
                            <input
                                value={createData.ownerFirstName}
                                onChange={(e) => setCreateData({ ...createData, ownerFirstName: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Last name <span className="text-gray-400 normal-case font-medium">(optional)</span></label>
                            <input
                                value={createData.ownerLastName}
                                onChange={(e) => setCreateData({ ...createData, ownerLastName: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Owner email</label>
                            <input
                                required
                                type="email"
                                value={createData.ownerEmail}
                                onChange={(e) => setCreateData({ ...createData, ownerEmail: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Property address <span className="text-gray-400 normal-case font-medium">(optional)</span></label>
                            <input
                                value={createData.propertyAddress}
                                onChange={(e) => setCreateData({ ...createData, propertyAddress: e.target.value })}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Signature mode</label>
                        <select
                            value={createData.signatureMode || 'DRAW_OR_UPLOAD'}
                            onChange={(e) => setCreateData({ ...createData, signatureMode: e.target.value as CreateOnboardingRequest['signatureMode'] })}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                        >
                            <option value="DRAW_OR_UPLOAD">Draw / upload signature</option>
                            <option value="OTP">Email OTP signature</option>
                        </select>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setShowCreate(false)}
                            className="px-5 py-2.5 rounded-xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={creating}
                            className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                        >
                            {creating ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                            Create &amp; send link
                        </button>
                    </div>
                </form>
            )}

            {/* List */}
            {loading ? (
                <div className="flex items-center justify-center py-20 text-gray-400">
                    <Loader className="w-8 h-8 animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                    <p className="font-medium">No onboardings yet. Create one to get started.</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((item) => (
                        <div key={item.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="font-bold text-gray-900 truncate">
                                            {(item.ownerFirstName || '') + ' ' + (item.ownerLastName || '')}
                                            {!item.ownerFirstName && !item.ownerLastName && item.ownerEmail}
                                        </h4>
                                        <span className="text-xs font-bold uppercase tracking-wider text-brand bg-brand/10 px-2 py-0.5 rounded-full">
                                            {item.status}
                                        </span>
                                        {item.signatureMode && (
                                            <span className="text-xs font-bold uppercase tracking-wider text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                                                {SIGNATURE_MODE_LABELS[item.signatureMode] || item.signatureMode}
                                            </span>
                                        )}
                                        {item.signatureMethod && (
                                            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                                                {SIGNATURE_METHOD_LABELS[item.signatureMethod] || item.signatureMethod}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 truncate">{item.ownerEmail}</p>
                                    {item.propertyAddress && (
                                        <p className="text-xs text-gray-400 truncate">{item.propertyAddress}</p>
                                    )}
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        <StepPill label="Property setup" done={item.propertySetupCompleted} />
                                        <StepPill label="Owner form" done={item.ownerFormCompleted} />
                                        <StepPill label="Signed" done={!!item.signedAt} />
                                        <StepPill label="Certified" done={!!item.certVerifyUrl} />
                                    </div>

                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        onClick={() => handleCopy(item)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                                        title="Copy link"
                                    >
                                        {copiedId === item.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                        {copiedId === item.id ? 'Copied' : 'Copy link'}
                                    </button>
                                    <button
                                        onClick={() => handleResend(item)}
                                        disabled={resendingId === item.id}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all disabled:opacity-60"
                                        title="Resend link"
                                    >
                                        {resendingId === item.id ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                        Resend
                                    </button>
                                    {item.signedAt && (
                                        <button
                                            onClick={() => setDocsTarget(item)}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold border border-brand/40 text-brand px-3 py-2 rounded-lg hover:bg-brand/5 transition-all"
                                            title="Download signed documents"
                                        >
                                            <Download className="w-4 h-4" /> Documents
                                        </button>
                                    )}
                                    {!item.signedAt && (
                                        <button
                                            onClick={() => openEdit(item)}
                                            className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                                            title="Edit owner details"
                                        >
                                            <UserCog className="w-4 h-4" /> Edit
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleViewData(item)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                                        title="View sensitive data"
                                    >
                                        <Eye className="w-4 h-4" /> Data
                                    </button>
                                    <button
                                        onClick={() => { setAmendTarget(item); setAmendDoc('cohosting'); setAmendRef(''); setAmendText(''); }}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                                        title="Manage authorized modifications"
                                    >
                                        <FileEdit className="w-4 h-4" /> Modifications
                                        {amendmentCount(item) > 0 && (
                                            <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold">
                                                {amendmentCount(item)}
                                            </span>
                                        )}
                                    </button>
                                    {item.certVerifyUrl && (
                                        <a
                                            href={item.certVerifyUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 text-xs font-bold border border-gray-300 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-all"
                                            title="Verify certification"
                                        >
                                            <ExternalLink className="w-4 h-4" /> Verify
                                        </a>
                                    )}
                                    <button
                                        onClick={() => handleDelete(item)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 border border-red-200 px-3 py-2 rounded-lg hover:bg-red-50 transition-all"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Sensitive data modal */}
            {(detailLoading || detail) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h3 className="font-serif font-bold text-xl text-primary">Sensitive data</h3>
                            <button
                                onClick={() => setDetail(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            {detailLoading ? (
                                <div className="flex items-center justify-center py-16 text-gray-400">
                                    <Loader className="w-8 h-8 animate-spin" />
                                </div>
                            ) : detail ? (
                                <div className="space-y-6">
                                    <div>
                                        <h4 className="font-bold text-sm uppercase tracking-wider text-gray-400 mb-3">Owner form</h4>
                                        {detail.ownerForm ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                                {Object.entries(detail.ownerForm).map(([k, v]) => (
                                                    <div key={k} className="flex justify-between gap-4 border-b border-gray-50 py-1">
                                                        <span className="text-gray-500">{k}</span>
                                                        <span className="font-semibold text-gray-800 text-right break-all">{String(v || '—')}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400">Not submitted yet.</p>
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-sm uppercase tracking-wider text-gray-400 mb-3">Property setup</h4>
                                        {detail.propertySetup ? (
                                            <div className="space-y-4">
                                                {detail.propertySetup.sections.map((sec) => (
                                                    <div key={sec.title}>
                                                        <p className="font-bold text-sm text-gray-700 mb-1">{sec.title}</p>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                                            {sec.items
                                                                .filter((it) => it.value !== '' && it.value !== false)
                                                                .map((it) => (
                                                                    <div key={it.label} className="flex justify-between gap-4 py-0.5">
                                                                        <span className="text-gray-500">{it.label}</span>
                                                                        <span className="font-semibold text-gray-800 text-right break-all">
                                                                            {it.type === 'checkbox' ? 'Yes' : String(it.value)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400">Not submitted yet.</p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal gestione note di modifica autorizzate */}
            {amendTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div>
                                <h3 className="font-serif font-bold text-xl text-primary">Authorized modifications</h3>
                                <p className="text-sm text-gray-400">
                                    {(amendTarget.ownerFirstName || '') + ' ' + (amendTarget.ownerLastName || '')} · {amendTarget.ownerEmail}
                                </p>
                            </div>
                            <button
                                onClick={() => setAmendTarget(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-6">
                            {amendTarget.signedAt ? (
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
                                    This onboarding has been signed and its documents are certified. Modifications can no longer be changed.
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 space-y-4">
                                    <p className="font-bold text-sm text-gray-700">Add a modification</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Document</label>
                                            <select
                                                value={amendDoc}
                                                onChange={(e) => setAmendDoc(e.target.value as AmendmentDocKey)}
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                            >
                                                {AMENDMENT_DOC_KEYS.map((k) => (
                                                    <option key={k} value={k}>{AMENDMENT_DOC_LABELS[k]}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Reference (optional)</label>
                                            <input
                                                value={amendRef}
                                                onChange={(e) => setAmendRef(e.target.value)}
                                                placeholder="e.g. Section 4.1 (Fee)"
                                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Modification text</label>
                                        <textarea
                                            value={amendText}
                                            onChange={(e) => setAmendText(e.target.value)}
                                            rows={3}
                                            placeholder="Describe the authorized change…"
                                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm resize-y"
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={handleAddAmendment}
                                            disabled={amendSaving}
                                            className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                                        >
                                            {amendSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                            Add modification
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Elenco note esistenti raggruppate per documento */}
                            <div className="space-y-5">
                                {amendmentCount(amendTarget) === 0 ? (
                                    <p className="text-sm text-gray-400">No modifications yet.</p>
                                ) : (
                                    AMENDMENT_DOC_KEYS
                                        .filter((k) => (amendTarget.amendments?.[k]?.length ?? 0) > 0)
                                        .map((k) => (
                                            <div key={k}>
                                                <p className="font-bold text-sm text-gray-700 mb-2">{AMENDMENT_DOC_LABELS[k]}</p>
                                                <div className="space-y-2">
                                                    {(amendTarget.amendments?.[k] ?? []).map((am, i) => (
                                                        <div key={i} className="flex items-start gap-3 border border-gray-200 rounded-xl p-3">
                                                            <span className="text-xs font-bold text-gray-400 mt-0.5">{i + 1}.</span>
                                                            <div className="flex-1 min-w-0">
                                                                {am.reference && <p className="font-semibold text-sm text-gray-800">{am.reference}</p>}
                                                                <p className="text-sm text-gray-600 break-words">{am.text}</p>
                                                            </div>
                                                            {!amendTarget.signedAt && (
                                                                <button
                                                                    onClick={() => handleRemoveAmendment(k, i)}
                                                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors shrink-0"
                                                                    title="Remove"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal documenti firmati (download) */}
            {docsTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div>
                                <h3 className="font-serif font-bold text-xl text-primary">Signed documents</h3>
                                <p className="text-sm text-gray-400">
                                    {(docsTarget.ownerFirstName || '') + ' ' + (docsTarget.ownerLastName || '')} · {docsTarget.ownerEmail}
                                </p>
                            </div>
                            <button
                                onClick={() => setDocsTarget(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-3">
                            {(docsTarget.documents && docsTarget.documents.length > 0) ? (
                                docsTarget.documents.map((d, i) => (
                                    <a
                                        key={i}
                                        href={d.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-3 border border-gray-200 rounded-xl p-3 hover:bg-gray-50 transition-all"
                                    >
                                        <FileText className="w-5 h-5 text-brand shrink-0" />
                                        <span className="flex-1 text-sm font-semibold text-gray-800">{d.label}</span>
                                        <Download className="w-4 h-4 text-gray-400" />
                                    </a>
                                ))
                            ) : (
                                <p className="text-sm text-gray-400">
                                    Documents are not available yet. If the onboarding was just signed, certification may still be in progress — click Refresh and try again.
                                </p>
                            )}

                            {docsTarget.certificateUrl && (
                                <a
                                    href={docsTarget.certificateUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-xl p-3 hover:bg-emerald-100 transition-all"
                                >
                                    <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                                    <span className="flex-1 text-sm font-semibold text-emerald-800">Certificate of Authenticity</span>
                                    <Download className="w-4 h-4 text-emerald-500" />
                                </a>
                            )}

                            {docsTarget.certVerifyUrl && (
                                <a
                                    href={docsTarget.certVerifyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-700"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" /> Verify on blockchain
                                </a>
                            )}

                            <p className="text-xs text-gray-400 pt-1">
                                Download links are temporary (about 1 hour). If one stops working, close this and click Refresh.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal modifica dati anagrafici (admin) */}
            {editTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <h3 className="font-serif font-bold text-xl text-primary">Edit owner details</h3>
                            <button
                                onClick={() => setEditTarget(null)}
                                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">First name</label>
                                    <input
                                        value={editForm.ownerFirstName ?? ''}
                                        onChange={(e) => setEditForm((f) => ({ ...f, ownerFirstName: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Last name</label>
                                    <input
                                        value={editForm.ownerLastName ?? ''}
                                        onChange={(e) => setEditForm((f) => ({ ...f, ownerLastName: e.target.value }))}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Owner email</label>
                                <input
                                    type="email"
                                    value={editForm.ownerEmail ?? ''}
                                    onChange={(e) => setEditForm((f) => ({ ...f, ownerEmail: e.target.value }))}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                />
                                <p className="text-xs text-gray-400 mt-1">Changing the email affects where the access code (OTP) is sent. Resend the link afterwards if needed.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Property address</label>
                                <input
                                    value={editForm.propertyAddress ?? ''}
                                    onChange={(e) => setEditForm((f) => ({ ...f, propertyAddress: e.target.value }))}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-100">
                            <button
                                onClick={() => setEditTarget(null)}
                                className="px-5 py-2.5 rounded-xl font-bold border border-gray-300 text-gray-600 hover:bg-gray-100 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                disabled={editSaving}
                                className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-xl font-bold hover:bg-[#c42e40] shadow-md transition-all disabled:opacity-60"
                            >
                                {editSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                Save changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingAdminPanel;
